/**
 * Anonymous draft storage (audit H5, journey §9).
 *
 * Signed-out shoppers have no `CustomerPreferences` / `Customer` rows. Their
 * in-progress selections are held client-side (`PreferencesModal`,
 * ConnectContextModal). Data is persisted in **`localStorage`** with a **28-day TTL**
 * (sliding freshness on save) so closing the browser does not discard choices;
 * drafts older than TTL are dropped on read.
 *
 * Legacy payloads from tab-only **`sessionStorage`** (older builds) are read once,
 * migrated to `localStorage`, and removed from `sessionStorage`.
 */

const PREFS_KEY = "fg.anonDraft.preferences";
const PHYSIOLOGY_KEY = "fg.anonDraft.physiology";
/** Average running pace (seconds per mile) for guests — promoted on login. */
const RUNNING_PACE_KEY = "fg.anonDraft.runningPaceSecPerMi";
/** Legacy single-slot key — folded into {@link PENDING_RACES_KEY} on first read. */
const PENDING_RACE_KEY = "fg.anonDraft.pendingRaceEvent";
const PENDING_RACES_KEY = "fg.anon.pendingRaceEvents";

/** Keep aligned with storefront promise: guest preferences linger ~four weeks on device. */
const DRAFT_TTL_MS = 28 * 24 * 60 * 60 * 1000;

const ENV = 1;

type Wrapped<T> = { _fgAnonDraft: typeof ENV; savedAt: number; data: T };

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function tryRemoveLegacySession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Parse stored JSON — wrapped (TTL) or legacy bare object. */
function unwrap<T>(key: string, parsed: unknown): T | null {
  if (
    parsed &&
    typeof parsed === "object" &&
    "_fgAnonDraft" in parsed &&
    (parsed as Wrapped<T>)._fgAnonDraft === ENV &&
    "savedAt" in parsed &&
    "data" in parsed
  ) {
    const w = parsed as Wrapped<T>;
    if (Date.now() - w.savedAt > DRAFT_TTL_MS) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      tryRemoveLegacySession(key);
      return null;
    }
    return w.data;
  }
  /* Legacy bare JSON — hydrate once, restart TTL from migration. */
  return parsed as T;
}

function readJson<T>(key: string): T | null {
  if (!isBrowser()) return null;
  try {
    let raw = localStorage.getItem(key);
    let fromSession = false;
    if (raw == null) {
      try {
        raw = sessionStorage.getItem(key);
        fromSession = raw != null;
      } catch {
        raw = null;
      }
    }
    if (raw == null) return null;

    const parsed = JSON.parse(raw) as unknown;
    const data = unwrap<T>(key, parsed);
    if (data == null) return null;

    if (fromSession) {
      tryRemoveLegacySession(key);
    }

    /* Re-save legacy unwrapped payloads so TTL applies going forward */
    const needsWrap =
      !parsed ||
      typeof parsed !== "object" ||
      !("_fgAnonDraft" in parsed) ||
      (parsed as Wrapped<T>)._fgAnonDraft !== ENV;
    if (needsWrap) {
      writeJson(key, data);
    }

    return data;
  } catch {
    return null;
  }
}

function writeJson<T>(key: string, value: T | null): void {
  if (!isBrowser()) return;
  try {
    tryRemoveLegacySession(key);
    if (value == null) {
      localStorage.removeItem(key);
      return;
    }
    const wrapped: Wrapped<T> = {
      _fgAnonDraft: ENV,
      savedAt: Date.now(),
      data: value
    };
    localStorage.setItem(key, JSON.stringify(wrapped));
  } catch {
    /* quota / disabled — best-effort */
  }
}

export function getAnonPreferencesDraft<T>(): T | null {
  return readJson<T>(PREFS_KEY);
}

export function setAnonPreferencesDraft<T>(value: T | null): void {
  writeJson(PREFS_KEY, value);
}

export function getAnonPhysiologyDraft<T>(): T | null {
  return readJson<T>(PHYSIOLOGY_KEY);
}

export function setAnonPhysiologyDraft<T>(value: T | null): void {
  writeJson(PHYSIOLOGY_KEY, value);
}

export function getAnonRunningPaceDraft(): number | null {
  const v = readJson<number>(RUNNING_PACE_KEY);
  return typeof v === "number" && Number.isInteger(v) ? v : null;
}

export function setAnonRunningPaceDraft(secPerMi: number | null): void {
  writeJson(RUNNING_PACE_KEY, secPerMi);
}

/**
 * A race a guest picked that reached NO server row, held here until the login
 * replay (`anonRaceReplay.ts`) creates it as a real `CustomerEvent`.
 *
 * Narrowed by customer-event-anon-ownable: anon sessions DO own `CustomerEvent`s
 * now, so every path that reaches a journey create — `POST /api/journeys` with a
 * `raceDate`, and `applyTrainingPlan` — persists the guest's race as a durable
 * anon-owned row instead and writes nothing here. What still lands in this store:
 *   - a tune-up/milestone setup (no journey create at all, and the server's
 *     `resolveNewAsMilestone` returns no event for an anon principal);
 *   - the "just save the event" exit from the window sheet (the funnel's
 *     `createEvent` is customer-only);
 *   - drafts written by an OLDER build, still on returning visitors' devices.
 * Exactly one of {eventCatalogId, custom name+date} identifies the race.
 */
export type AnonPendingRaceEvent = {
  name: string;
  /** `YYYY-MM-DD`. */
  dateYmd: string;
  /** Set when the race was picked from the shared catalog (else a custom race). */
  eventCatalogId?: string;
  /** The catalog distance the guest chose (replayed when linking on login). */
  distanceLabel?: string;
  /**
   * LEGACY FIELD — no current write site sets it. The applied plan's
   * `planGroupId`: on login the replayed `CustomerEvent` is linked onto this
   * group's workouts (which merged with no event). Plan applies now stamp the
   * workouts with a durable anon event at apply time, so only drafts from an
   * older build carry this; the link step is skipped when absent.
   */
  planGroupId?: string;
  /** J4 intent chosen by the guest (journeys-overhaul P2). Absent ⇒ "goal". */
  intent?: "goal" | "milestone";
  /** LEGACY FIELD — the journey window start the guest confirmed. Only the
   *  journey-creating paths ever set it, and those no longer draft. */
  startYmd?: string;
  /**
   * LEGACY FIELD — no current write site sets it. The anon `build` journey
   * created by the funnel (no plan group): on login, once the journey merges to
   * the customer, the replayed event was linked onto it via `link-event`. Build
   * creates now carry a durable anon `customerEventId` of their own.
   */
  journeyId?: string;
};

/**
 * Same held race? Catalog drafts match on `eventCatalogId` + `distanceLabel`
 * (the same catalog race at two distances is two drafts); custom drafts match
 * on trimmed `name` + `dateYmd`. A catalog draft never matches a custom one —
 * they identify differently, so name+date collisions across the classes are
 * kept separate.
 */
export function anonPendingRaceEventsMatch(
  a: AnonPendingRaceEvent,
  b: AnonPendingRaceEvent
): boolean {
  if (a.eventCatalogId || b.eventCatalogId) {
    return a.eventCatalogId === b.eventCatalogId && a.distanceLabel === b.distanceLabel;
  }
  return a.name.trim() === b.name.trim() && a.dateYmd === b.dateYmd;
}

/**
 * All races a guest has set up this session, insertion-ordered (multi-slot —
 * a goal + a prep race resolved via the collision sheet both survive to the
 * login replay). Reads through the legacy single-slot key once: any value
 * there is folded into the array (skipped if it duplicates an entry), the
 * array is persisted, and the old key removed.
 */
export function getAnonPendingRaceEvents(): AnonPendingRaceEvent[] {
  const stored = readJson<AnonPendingRaceEvent[]>(PENDING_RACES_KEY);
  const list = Array.isArray(stored) ? stored : [];
  const legacy = readJson<AnonPendingRaceEvent>(PENDING_RACE_KEY);
  if (legacy == null) return list;
  const migrated = list.some((e) => anonPendingRaceEventsMatch(e, legacy))
    ? list
    : [...list, legacy];
  writeJson(PENDING_RACES_KEY, migrated);
  writeJson(PENDING_RACE_KEY, null); // one-shot: old key removed after folding
  return migrated;
}

/**
 * Hold (or refresh) a race draft. If an entry for the same race exists (per
 * {@link anonPendingRaceEventsMatch}) it is REPLACED in place — newest wins —
 * otherwise the draft is appended, preserving setup order for the replay.
 */
export function addAnonPendingRaceEvent(value: AnonPendingRaceEvent): void {
  const list = getAnonPendingRaceEvents();
  const idx = list.findIndex((e) => anonPendingRaceEventsMatch(e, value));
  const next = idx >= 0 ? list.map((e, i) => (i === idx ? value : e)) : [...list, value];
  writeJson(PENDING_RACES_KEY, next);
}

/** Drop the entry matching `match` (per {@link anonPendingRaceEventsMatch}), if any. */
export function removeAnonPendingRaceEvent(match: AnonPendingRaceEvent): void {
  const list = getAnonPendingRaceEvents();
  const idx = list.findIndex((e) => anonPendingRaceEventsMatch(e, match));
  if (idx < 0) return;
  const next = list.filter((_, i) => i !== idx);
  writeJson(PENDING_RACES_KEY, next.length > 0 ? next : null);
}

/** Drop every held race draft (and the legacy single-slot key). */
export function clearAnonPendingRaceEvents(): void {
  writeJson(PENDING_RACES_KEY, null);
  writeJson(PENDING_RACE_KEY, null);
}

/**
 * Back-compat wrapper over the multi-slot store: returns the FIRST held draft
 * (oldest — insertion order) or null. Every consumer in the app now reads the
 * multi-entry {@link getAnonPendingRaceEvents} (including `journeyApi.ts`'s
 * `journeyRaceName` fallback); this is kept only for out-of-tree callers.
 */
export function getAnonPendingRaceEvent(): AnonPendingRaceEvent | null {
  return getAnonPendingRaceEvents()[0] ?? null;
}

/**
 * Back-compat wrapper over the multi-slot store:
 *   - `setAnonPendingRaceEvent(null)`  → {@link clearAnonPendingRaceEvents} (all slots)
 *   - `setAnonPendingRaceEvent(draft)` → {@link addAnonPendingRaceEvent} (dedupe/replace)
 * Kept so untouched call sites compile unchanged; the `useEventSetup` write
 * sites already use `addAnonPendingRaceEvent` directly.
 */
export function setAnonPendingRaceEvent(value: AnonPendingRaceEvent | null): void {
  if (value == null) {
    clearAnonPendingRaceEvents();
  } else {
    addAnonPendingRaceEvent(value);
  }
}
